import { Save, Undo2 } from 'lucide-react'
import { useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage, toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { EmptyState } from '@/components/feedback/States'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { cn } from '@/utils/cn'
import { parseMark } from '../schemas'
import { formatMarks } from '../status'
import { sectionLabel } from '@/features/academic/sectionLabel'
import type { MarkEntry, MarkSheet, MarkSheetStudent } from '../types'

interface Row {
  marks: string
  isAbsent: boolean
  remarks: string
}

const toRow = (s: MarkSheetStudent): Row => ({
  marks: s.marksObtained === null ? '' : formatMarks(s.marksObtained),
  isAbsent: s.isAbsent,
  remarks: s.remarks ?? '',
})
const same = (a: Row, b: Row) =>
  a.marks.trim() === b.marks.trim() && a.isAbsent === b.isAbsent && a.remarks.trim() === b.remarks.trim()

interface MarkEntryGridProps {
  sheet: MarkSheet
  /** Persists the changed rows and resolves with the refreshed sheet. */
  onSave: (entries: MarkEntry[]) => Promise<MarkSheet>
}

/**
 * Spreadsheet-like mark entry for one paper × section. Enter / ↓ move to the next student, ↑ to
 * the previous one; absent is a separate toggle (absent ≠ zero). Only changed rows are sent.
 */
export function MarkEntryGrid({ sheet, onSave }: MarkEntryGridProps) {
  const { t } = useTranslation('exams')
  const [baseline, setBaseline] = useState<Record<number, Row>>(() =>
    Object.fromEntries(sheet.students.map((s) => [s.studentId, toRow(s)])),
  )
  const [rows, setRows] = useState<Record<number, Row>>(baseline)
  const [saving, setSaving] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<number, string>>({})
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  const max = sheet.paper.maxMarks
  const readOnly = !sheet.canEdit

  const errors = useMemo(() => {
    const out: Record<number, string> = {}
    for (const s of sheet.students) {
      const row = rows[s.studentId]
      if (!row || row.isAbsent) continue
      const { error } = parseMark(row.marks, max)
      if (error) out[s.studentId] = error
    }
    return out
  }, [rows, sheet.students, max])

  const changed = sheet.students.filter((s) => {
    const a = rows[s.studentId]
    const b = baseline[s.studentId]
    return a && b && !same(a, b)
  })
  const errorCount = Object.keys(errors).length
  const enteredCount = sheet.students.filter((s) => {
    const r = rows[s.studentId]
    return r ? r.isAbsent || r.marks.trim() !== '' : false
  }).length

  const update = (id: number, patch: Partial<Row>) => {
    setRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { marks: '', isAbsent: false, remarks: '' }), ...patch },
    }))
    setServerErrors((prev) =>
      id in prev ? Object.fromEntries(Object.entries(prev).filter(([key]) => Number(key) !== id)) : prev,
    )
  }

  const focusRow = (index: number) => {
    const el = inputs.current[index]
    if (el) {
      el.focus()
      el.select()
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault()
      focusRow(Math.min(index + 1, sheet.students.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      focusRow(Math.max(index - 1, 0))
    }
  }

  const save = async () => {
    const entries: MarkEntry[] = changed.map((s) => {
      const r = rows[s.studentId] ?? { marks: '', isAbsent: false, remarks: '' }
      return {
        studentId: s.studentId,
        isAbsent: r.isAbsent,
        marksObtained: r.isAbsent ? null : parseMark(r.marks, max).value,
        remarks: r.remarks.trim() === '' ? null : r.remarks.trim(),
      }
    })
    setSaving(true)
    try {
      const fresh = await onSave(entries)
      const next = Object.fromEntries(fresh.students.map((s) => [s.studentId, toRow(s)]))
      setBaseline(next)
      setRows(next)
      setServerErrors({})
      toast.success(t('Marks saved'), t('{{count}} students updated.', { count: entries.length }))
    } catch (e) {
      const apiError = toApiError(e)
      const byStudent: Record<number, string> = {}
      for (const v of apiError.violations) {
        const m = /^entries\[(\d+)\]/.exec(v.field)
        const entry = m ? entries[Number(m[1])] : undefined
        if (entry) byStudent[entry.studentId] = v.message
      }
      setServerErrors(byStudent)
      toast.error(t('Marks were not saved'), errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  if (sheet.students.length === 0) {
    return (
      <EmptyState
        title={t('No students in this section')}
        description={t("Nobody is enrolled in this section for the exam's academic year.")}
        compact
      />
    )
  }

  return (
    <div>
      {readOnly ? (
        <div className="p-4">
          <Alert tone="info" title={t('Read only')}>
            {sheet.exam.status === 'published' || sheet.exam.status === 'locked'
              ? t('Results are published, so marks can no longer change here.')
              : t('Marks entry has not been opened for this exam yet.')}
          </Alert>
        </div>
      ) : null}
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <caption className="sr-only">
            {t('Marks for {{subject}}, {{group}}, out of {{max}}', {
              subject: sheet.paper.subjectName,
              group: sectionLabel(sheet.section.className, sheet.section.name, ' – '),
              max,
            })}
          </caption>
          <thead>
            <tr className="text-left text-2xs font-semibold tracking-wider text-muted uppercase">
              <th
                scope="col"
                className="sticky top-0 w-14 border-b border-line bg-surface-muted px-4 py-2.5 pl-5"
              >
                {t('Roll')}
              </th>
              <th scope="col" className="sticky top-0 border-b border-line bg-surface-muted px-4 py-2.5">
                {t('Student')}
              </th>
              <th scope="col" className="sticky top-0 w-40 border-b border-line bg-surface-muted px-4 py-2.5">
                {t('Marks')} <span className="normal-case">/ {formatMarks(max)}</span>
              </th>
              <th
                scope="col"
                className="sticky top-0 w-24 border-b border-line bg-surface-muted px-4 py-2.5 text-center"
              >
                {t('Absent')}
              </th>
              <th
                scope="col"
                className="sticky top-0 hidden border-b border-line bg-surface-muted px-4 py-2.5 pr-5 md:table-cell"
              >
                {t('Remarks')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sheet.students.map((s, index) => {
              const row = rows[s.studentId] ?? { marks: '', isAbsent: false, remarks: '' }
              const error = serverErrors[s.studentId] ?? errors[s.studentId]
              const dirty = !same(row, baseline[s.studentId] ?? row)
              const errorId = `mark-error-${s.studentId}`
              const failing =
                !row.isAbsent &&
                !error &&
                row.marks.trim() !== '' &&
                Number(row.marks) < sheet.paper.passMarks
              return (
                <tr key={s.studentId} className={cn('transition-colors', dirty && 'bg-brand-50/40')}>
                  <td className="border-b border-line px-4 py-2 pl-5 text-muted tabular">
                    {s.rollNumber ?? '—'}
                  </td>
                  <td className="border-b border-line px-4 py-2">
                    <p className="font-medium text-ink">{s.fullName}</p>
                    <p className="text-xs text-muted">{s.studentCode}</p>
                  </td>
                  <td className="border-b border-line px-4 py-2 align-top">
                    <div className="flex items-center gap-2">
                      <input
                        ref={(el) => {
                          inputs.current[index] = el
                        }}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        aria-label={t('Marks for {{name}}', { name: s.fullName })}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errorId : undefined}
                        disabled={readOnly || row.isAbsent}
                        placeholder={row.isAbsent ? t('Absent') : '–'}
                        value={row.isAbsent ? '' : row.marks}
                        onChange={(e) => update(s.studentId, { marks: e.target.value })}
                        onKeyDown={(e) => onKeyDown(e, index)}
                        onFocus={(e) => e.target.select()}
                        className={cn(
                          'h-9 w-24 rounded-control border border-line bg-surface px-2.5 text-right text-sm text-ink tabular shadow-xs transition-[border-color,box-shadow] focus:border-brand-400 focus:ring-4 focus:ring-brand-500/15 focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-sunken',
                          error && 'border-danger-500 focus:ring-danger-500/15',
                          failing && 'text-danger-700',
                        )}
                      />
                      {failing ? (
                        <Badge tone="danger" size="sm">
                          {t('Below pass')}
                        </Badge>
                      ) : null}
                    </div>
                    {error ? (
                      <p id={errorId} className="mt-1 text-xs font-medium text-danger-600" role="alert">
                        {t(error)}
                      </p>
                    ) : null}
                  </td>
                  <td className="border-b border-line px-4 py-2 text-center">
                    <Checkbox
                      aria-label={t('{{name}} was absent', { name: s.fullName })}
                      checked={row.isAbsent}
                      disabled={readOnly}
                      onCheckedChange={(v) => update(s.studentId, { isAbsent: v === true })}
                    />
                  </td>
                  <td className="hidden border-b border-line px-4 py-2 pr-5 md:table-cell">
                    <input
                      type="text"
                      aria-label={t('Remarks for {{name}}', { name: s.fullName })}
                      maxLength={255}
                      disabled={readOnly}
                      value={row.remarks}
                      onChange={(e) => update(s.studentId, { remarks: e.target.value })}
                      className="h-9 w-full min-w-40 rounded-control border border-transparent bg-transparent px-2.5 text-sm text-ink-soft hover:border-line focus:border-brand-400 focus:bg-surface focus:ring-4 focus:ring-brand-500/15 focus:outline-none disabled:cursor-not-allowed"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!readOnly ? (
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 rounded-b-card border-t border-line bg-surface/95 px-5 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted" aria-live="polite">
            <span className="font-medium text-ink tabular">{enteredCount}</span>{' '}
            {t('of {{total}} entered', { total: sheet.students.length })}
            {changed.length ? (
              <>
                {' '}
                ·{' '}
                <span className="font-medium text-brand-700">
                  {t('{{count}} unsaved', { count: changed.length })}
                </span>
              </>
            ) : null}
            {errorCount ? (
              <>
                {' '}
                ·{' '}
                <span className="font-medium text-danger-600">
                  {t('{{count}} to fix', { count: errorCount })}
                </span>
              </>
            ) : null}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leftIcon={<Undo2 />}
              disabled={!changed.length || saving}
              onClick={() => setRows(baseline)}
            >
              {t('Discard')}
            </Button>
            <Button
              leftIcon={<Save />}
              loading={saving}
              disabled={saving || !changed.length || errorCount > 0}
              onClick={() => void save()}
            >
              {t('Save marks')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
